/**
 * SoleadLab 2.0 — Indication Entity API Client
 *
 * V4 Phase 2.4/2.5: Frontend service for indication entity CRUD.
 *
 * Read (P2.4): fetchEntityIndications — authority-based read switch
 * Write (P2.5): createEntityIndication, updateEntityIndication, deleteEntityIndication
 *
 * Transforms between frontend shape (geometry object, properties object)
 * and entity row shape (geometry_json string, properties_json string).
 */

import type { Indication, IndicationType } from '@/stores/projectStore';
import { apiFetch, apiFetchWithEtag } from './apiFetch';
export { isConflictError } from './apiFetch';

// ─── Types ──────────────────────────────────────────────────────────────────────

/** Raw row shape returned by the entity API */
interface IndicationEntityRow {
  id: string;
  site_id: string;
  type: string;
  geometry_json: string;
  properties_json: string;
  observation_id: string | null;
  evidence_id: string | null;
  asset_id: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

/** Paginated list response (includes migration authority signal) */
interface IndicationListResponse {
  items: IndicationEntityRow[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  migrated: boolean;
}

/** Result of fetching entity indications — includes authority signal */
export interface EntityIndicationResult {
  migrated: boolean;
  indications: Indication[];
}

// ─── Transform ──────────────────────────────────────────────────────────────────

/** Entity row → frontend Indication shape */
function entityRowToIndication(row: IndicationEntityRow): Indication {
  let geometry: GeoJSON.Geometry;
  try {
    geometry = JSON.parse(row.geometry_json);
  } catch {
    geometry = { type: 'Point', coordinates: [0, 0] };
  }

  let properties: Record<string, unknown>;
  try {
    properties = JSON.parse(row.properties_json);
  } catch {
    properties = {};
  }

  if (!properties.createdAt) properties.createdAt = row.created_at;
  if (!properties.updatedAt) properties.updatedAt = row.updated_at;

  return {
    id: row.id,
    type: row.type as IndicationType,
    geometry,
    properties: properties as unknown as Indication['properties'],
    _version: row.version,
  };
}

/** Frontend Indication → entity API body shape */
function indicationToEntityBody(ind: Indication): {
  id: string;
  type: string;
  geometry_json: string;
  properties_json: string;
} {
  return {
    id: ind.id,
    type: ind.type,
    geometry_json: JSON.stringify(ind.geometry),
    properties_json: JSON.stringify(ind.properties),
  };
}

// ─── Read API ───────────────────────────────────────────────────────────────────

/**
 * Fetch ALL indications for a site from the entity API.
 * Paginates automatically (200 per page).
 * Returns indications + migration authority signal.
 */
export async function fetchEntityIndications(
  siteId: string,
): Promise<EntityIndicationResult> {
  const allIndications: Indication[] = [];
  let page = 1;
  let migrated = false;
  const limit = 200;

  while (true) {
    const result = await apiFetch<IndicationListResponse>(
      `/api/v2/sites/${siteId}/indications?page=${page}&limit=${limit}`,
    );

    if (page === 1) {
      migrated = result.migrated;
    }

    for (const row of result.items) {
      allIndications.push(entityRowToIndication(row));
    }

    if (!result.hasMore) break;
    page++;
  }

  return { migrated, indications: allIndications };
}

/**
 * Fetch a single indication by ID from the entity API.
 * Used for conflict reload — when a write returns 409, we fetch
 * the authoritative server state to replace stale local state.
 * Returns null if not found (404).
 */
export async function fetchSingleEntityIndication(
  siteId: string,
  id: string,
): Promise<Indication | null> {
  try {
    const row = await apiFetch<IndicationEntityRow>(
      `/api/v2/sites/${siteId}/indications/${id}`,
    );
    return entityRowToIndication(row);
  } catch {
    return null;
  }
}

// ─── Write API ──────────────────────────────────────────────────────────────────

/**
 * Create an indication in the entity table.
 * Returns the created indication with _version from server.
 */
export async function createEntityIndication(
  siteId: string,
  indication: Indication,
): Promise<Indication> {
  const body = indicationToEntityBody(indication);
  const { data: row, etag } = await apiFetchWithEtag<IndicationEntityRow>(
    `/api/v2/sites/${siteId}/indications`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  const result = entityRowToIndication(row);
  result._version = etag || result._version;
  return result;
}

/** Response of POST /indications/bulk — counts + conflict id arrays only,
 *  NO per-item created list. New rows are inserted with version=1, so the
 *  caller must stamp `_version: 1` locally on every sent id that is absent
 *  from conflicts[] AND staleConflicts[] (without the stamp, later edits of
 *  those indications skip the entity PATCH and are silently lost). */
export interface BulkCreateIndicationsResult {
  created: number;
  updated: number;
  skipped: number;
  conflicts: Array<{ id: string }>;
  staleConflicts: Array<{ id: string; currentVersion: number; yourVersion: number }>;
}

/**
 * POST /api/v2/sites/:siteId/indications/bulk — batch CREATE (max 200 items
 * per request; the caller chunks). Items are sent WITHOUT a version field →
 * create-only semantics: an id that already exists is skipped and returned
 * in conflicts[].
 */
export async function bulkCreateEntityIndications(
  siteId: string,
  indications: Indication[],
): Promise<BulkCreateIndicationsResult> {
  return apiFetch<BulkCreateIndicationsResult>(
    `/api/v2/sites/${siteId}/indications/bulk`,
    {
      method: 'POST',
      body: JSON.stringify({ items: indications.map(indicationToEntityBody) }),
    },
  );
}

/**
 * Update an indication in the entity table.
 * Requires _version for optimistic concurrency (If-Match header).
 * Returns the updated indication with new _version.
 */
export async function updateEntityIndication(
  siteId: string,
  id: string,
  updates: { type?: string; geometry?: GeoJSON.Geometry; properties?: Record<string, unknown> },
  version: number,
): Promise<Indication> {
  const patchBody: Record<string, string> = {};
  if (updates.type !== undefined) patchBody.type = updates.type;
  if (updates.geometry !== undefined) patchBody.geometry_json = JSON.stringify(updates.geometry);
  if (updates.properties !== undefined) patchBody.properties_json = JSON.stringify(updates.properties);

  const { data: row, etag } = await apiFetchWithEtag<IndicationEntityRow>(
    `/api/v2/sites/${siteId}/indications/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patchBody),
      headers: { 'If-Match': String(version) },
    },
  );
  const result = entityRowToIndication(row);
  result._version = etag || result._version;
  return result;
}

/**
 * FL-1: field-link notes edit — PATCHes ONLY `{ description }` (the UI
 * "Notes" field). The backend's field branch merges it into the stored
 * properties server-side and rejects any other key for share visitors;
 * authenticated operators keep using updateEntityIndication instead.
 * apiFetch attaches the required X-SL-Field-Actor header in field mode.
 */
export async function updateEntityIndicationFieldNotes(
  siteId: string,
  id: string,
  description: string,
  version: number,
): Promise<Indication> {
  const { data: row, etag } = await apiFetchWithEtag<IndicationEntityRow>(
    `/api/v2/sites/${siteId}/indications/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ description }),
      headers: { 'If-Match': String(version) },
    },
  );
  const result = entityRowToIndication(row);
  result._version = etag || result._version;
  return result;
}

/**
 * FL-2.5: field-link geometry edit — only valid for indications created by
 * the SAME field link (the backend enforces ownership via createdVia).
 */
export async function updateEntityIndicationFieldGeometry(
  siteId: string,
  id: string,
  geometry: GeoJSON.Geometry,
  version: number,
  // Location-derived topology fields (station/inverter/…) recomputed for the
  // new geometry — the server merges them so a moved mark's topology follows.
  topologyUpdates?: Record<string, unknown>,
): Promise<Indication> {
  const { data: row, etag } = await apiFetchWithEtag<IndicationEntityRow>(
    `/api/v2/sites/${siteId}/indications/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        geometry_json: JSON.stringify(geometry),
        ...(topologyUpdates ? { topology_json: JSON.stringify(topologyUpdates) } : {}),
      }),
      headers: { 'If-Match': String(version) },
    },
  );
  const result = entityRowToIndication(row);
  result._version = etag || result._version;
  return result;
}

/**
 * FL-3a: attach a field photo to an indication (multipart POST). The backend
 * validates (image allowlist + magic bytes), stores under
 * sites/{siteId}/field/, appends a server-stamped entry to
 * properties.fieldPhotos, and ops-logs — returning the full updated row.
 * Deliberately NO If-Match: the append is commutative and the server resolves
 * version races itself (a 409 here would force re-uploading the bytes over
 * cell). Long timeout for slow field connections.
 */
export async function addEntityIndicationFieldPhoto(
  siteId: string,
  id: string,
  photo: Blob,
  fileName: string,
): Promise<Indication> {
  const form = new FormData();
  form.append('file', photo, fileName);
  const { data: row, etag } = await apiFetchWithEtag<IndicationEntityRow>(
    `/api/v2/sites/${siteId}/indications/${id}/photos`,
    { method: 'POST', body: form },
    120_000,
  );
  const result = entityRowToIndication(row);
  result._version = etag || result._version;
  return result;
}

/**
 * FL-3a: remove one own field photo (entry.linkId must match the caller's
 * link — server-enforced). Rides the field PATCH, so If-Match applies.
 */
export async function removeEntityIndicationFieldPhoto(
  siteId: string,
  id: string,
  photoId: string,
  version: number,
): Promise<Indication> {
  const { data: row, etag } = await apiFetchWithEtag<IndicationEntityRow>(
    `/api/v2/sites/${siteId}/indications/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ removeFieldPhotoId: photoId }),
      headers: { 'If-Match': String(version) },
    },
  );
  const result = entityRowToIndication(row);
  result._version = etag || result._version;
  return result;
}

/**
 * Transition an indication's resolution status (Phase 1).
 * POSTs to the server-stamped status endpoint, which appends the lifecycle
 * event (authoritative `at`/`by`) and returns the full updated indication.
 * Requires _version for optimistic concurrency (If-Match header).
 */
export async function updateEntityIndicationStatus(
  siteId: string,
  id: string,
  status: 'open' | 'in_progress' | 'resolved',
  version: number,
  note?: string,
): Promise<Indication> {
  const body: Record<string, string> = { status };
  if (note !== undefined) body.note = note;

  const { data: row, etag } = await apiFetchWithEtag<IndicationEntityRow>(
    `/api/v2/sites/${siteId}/indications/${id}/status`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'If-Match': String(version) },
    },
  );
  const result = entityRowToIndication(row);
  result._version = etag || result._version;
  return result;
}

/**
 * Delete an indication from the entity table.
 * Requires _version for optimistic concurrency (If-Match header).
 */
export async function deleteEntityIndication(
  siteId: string,
  id: string,
  version: number,
): Promise<void> {
  await apiFetch<void>(
    `/api/v2/sites/${siteId}/indications/${id}`,
    {
      method: 'DELETE',
      headers: { 'If-Match': String(version) },
    },
  );
}
